import { useMutation, useQuery } from '@tanstack/react-query';
import { Check, Search, UserPlus, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import type { Paginated } from '@barbeiropro/contracts';
import { Field } from '@/components/shared/field';
import { MaskedInput } from '@/components/shared/masked-input';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api, errorMessage, HttpError } from '@/lib/api';
import { digits, maskPhone } from '@/lib/format';
import type { CustomerOption } from './api';

function useDebounced<T>(value: T, ms = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** Busca (GET /customers?q=) e cadastro rápido (POST /customers). Esses endpoints pertencem ao módulo de Clientes. */
export function CustomerPicker({
  value,
  onChange,
  error,
  id = 'customer-search',
}: {
  value: CustomerOption | null;
  onChange: (c: CustomerOption | null) => void;
  error?: string;
  id?: string;
}) {
  const [term, setTerm] = useState('');
  const q = useDebounced(term.trim());
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ name: '', phone: '', email: '' });
  const [draftError, setDraftError] = useState<string | null>(null);

  const search = useQuery({
    queryKey: ['customers', 'picker', q],
    queryFn: () => api.get<Paginated<CustomerOption>>('/customers', { q, pageSize: 8 }),
    enabled: q.length >= 2 && !value,
    retry: false,
  });

  const create = useMutation({
    mutationFn: () =>
      api.post<CustomerOption>('/customers', {
        name: draft.name.trim(),
        phone: digits(draft.phone) || null,
        email: draft.email.trim() || null,
      }),
    onSuccess: (c) => {
      toast.success('Cliente cadastrado');
      onChange({ id: c.id, name: c.name ?? draft.name, phone: c.phone ?? null, email: c.email ?? null });
      setCreating(false);
      setDraft({ name: '', phone: '', email: '' });
    },
    onError: (e) => setDraftError(e instanceof HttpError && e.status === 404 ? 'O cadastro de clientes ainda não está disponível.' : errorMessage(e)),
  });

  if (value) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-md border bg-accent/40 px-3 py-2">
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 truncate text-sm font-medium">
            <Check className="size-4 text-primary" aria-hidden /> {value.name}
          </p>
          <p className="truncate text-xs text-muted-foreground">{[value.phone ? maskPhone(value.phone) : null, value.email].filter(Boolean).join(' · ') || 'Sem contato cadastrado'}</p>
        </div>
        <Button type="button" variant="ghost" size="icon-sm" aria-label="Trocar cliente" onClick={() => onChange(null)}>
          <X />
        </Button>
      </div>
    );
  }

  return (
    <div className="grid gap-2">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-2.5 size-4 text-muted-foreground" aria-hidden />
        <Input id={id} className="pl-8" placeholder="Buscar por nome, telefone ou e-mail" value={term} onChange={(e) => setTerm(e.target.value)} autoComplete="off" aria-invalid={Boolean(error)} />
      </div>
      {error && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
      {q.length >= 2 && (
        <div className="rounded-md border" aria-live="polite">
          {search.isLoading && <p className="px-3 py-2 text-sm text-muted-foreground">Buscando…</p>}
          {search.isError && (
            <p role="alert" className="px-3 py-2 text-sm text-destructive">
              {search.error instanceof HttpError && search.error.status === 404 ? 'A busca de clientes ainda não está disponível.' : errorMessage(search.error)}
            </p>
          )}
          {search.data && search.data.data.length === 0 && <p className="px-3 py-2 text-sm text-muted-foreground">Nenhum cliente encontrado.</p>}
          {search.data?.data.map((c) => (
            <button key={c.id} type="button" onClick={() => onChange(c)} className="flex w-full flex-col items-start px-3 py-2 text-left text-sm hover:bg-accent focus-visible:bg-accent focus-visible:outline-none [&:not(:last-child)]:border-b">
              <span className="font-medium">{c.name}</span>
              <span className="text-xs text-muted-foreground">{[c.phone ? maskPhone(c.phone) : null, c.email].filter(Boolean).join(' · ')}</span>
            </button>
          ))}
        </div>
      )}
      {!creating ? (
        <Button type="button" variant="outline" size="sm" className="justify-self-start" onClick={() => setCreating(true)}>
          <UserPlus /> Cadastrar novo cliente
        </Button>
      ) : (
        <div className="grid gap-3 rounded-md border p-3">
          <Field id="qc-name" label="Nome">
            <Input id="qc-name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field id="qc-phone" label="Telefone">
              <MaskedInput id="qc-phone" mask="phone" value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} />
            </Field>
            <Field id="qc-email" label="E-mail" hint="Necessário para enviar confirmação e lembrete">
              <Input id="qc-email" type="email" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} />
            </Field>
          </div>
          {draftError && (
            <p role="alert" className="text-xs text-destructive">
              {draftError}
            </p>
          )}
          <div className="flex gap-2">
            <Button type="button" size="sm" disabled={draft.name.trim().length < 2 || create.isPending} onClick={() => (setDraftError(null), create.mutate())}>
              Salvar cliente
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setCreating(false)}>
              Voltar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
